import styled from '@emotion/styled';
import type { CodeEnum } from '@zelos/codegen/apis';
import { classificationMeta } from '@zelos/shared/constants/meta';
import { formatNumber } from '@zelos/shared/lib/number-util';
import Image from 'next/image';
import { InnerContent, InnerTitle } from '..';
import TitleWithDivider from '@/components/comm/TitleWithDivider';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import calendarImg from '@/public/images/img-testicon-calendar.svg';
import noteImg from '@/public/images/img-testicon-category.svg';
import cylinderImg from '@/public/images/img-testicon-cylinder.svg';
import moneyImg from '@/public/images/img-testicon-money.svg';
import questionImg from '@/public/images/img-testicon-question.svg';
import clockImg from '@/public/images/img-testicon-testtime.svg';

const Container = styled.div`
  display: grid;
  row-gap: 60px;
`;

const InnerContainer = styled.div`
  display: grid;
`;

const CardContainer = styled.div`
  display: grid;
  margin-top: 20px;
  column-gap: 16px;
  grid-template-columns: repeat(3, 1fr);
  
  @media (max-width: ${breakpointMedium}) {
    grid-template-columns: 1fr;
    row-gap: 16px;
  }
`;

const Card = styled.div`
position: relative;
display: grid;
height: 136px;
padding: 28px;
border: 1px solid ${colors.lightGrey2};
border-radius: 15px;
grid-template-rows: repeat(3, 1fr);

@media (max-width: ${breakpointMedium}) {
  width: 100%;
  height: fit-content;
  grid-template-rows: repeat(2, 1fr);
}
`;

const ImgContainer = styled.div`
  position: absolute;
  right: 7%;
  bottom: 7%;
  padding-top: 100px;
`;

const CardTitle = styled.span`
color: ${colors.blue};
font-size: 16px;
font-weight: 500;

@media (max-width: ${breakpointSmall}) {
  font-size: 14px;
}
`;

const CardDesc = styled.span`
font-size: 18px;
font-weight: 700;

@media (max-width: ${breakpointSmall}) {
  font-size: 17px;
}
`;

const getFormattedBaseFee = (classification: CodeEnum) => {
  const baseFee = classificationMeta[classification].baseFee;
  return formatNumber(baseFee) + '원';
};

export const PCCPQualification = () => (
  <TitleWithDivider title="응시 자격 및 시험 안내">
    <Container>
      <InnerContainer>
        <InnerTitle>응시 자격 및 시험 안내</InnerTitle>
        <InnerContent>프로그래머스 코딩전문역량인증(PCCP)는 SW 전문가 및 상급 학습자를 대상으로 합니다.</InnerContent>
        <CardContainer>
          <Card>
            <CardTitle>시험시간</CardTitle>
            <CardDesc>120분</CardDesc>
            <ImgContainer>
              <Image src={clockImg} alt="clock" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>응시료</CardTitle>
            <CardDesc>{getFormattedBaseFee('PCCP')}</CardDesc>
            <ImgContainer>
              <Image src={moneyImg} alt="money" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>인증서 유효기간</CardTitle>
            <CardDesc>2년</CardDesc>
            <ImgContainer>
              <Image src={calendarImg} alt="calendar" />
            </ImgContainer>
          </Card>
        </CardContainer>
      </InnerContainer>
      <InnerContainer>
        <InnerTitle>시험 구성</InnerTitle>
        <InnerContent>4종 개발 언어 중 선택한 1개 언어로 4문항의 코드작성이 필요합니다.</InnerContent>
        <CardContainer>
          <Card>
            <CardTitle>시험과목 (택1)</CardTitle>
            <CardDesc>Python, JavaScript</CardDesc>
            <CardDesc>Java, C, C++, C#</CardDesc>
            <ImgContainer>
              <Image src={cylinderImg} alt="cylinder" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>문항수</CardTitle>
            <CardDesc>4 문항</CardDesc>
            <ImgContainer>
              <Image src={questionImg} alt="question" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>문제유형</CardTitle>
            <CardDesc>코드작성</CardDesc>
            <ImgContainer>
              <Image src={noteImg} alt="note" />
            </ImgContainer>
          </Card>
        </CardContainer>
      </InnerContainer>
    </Container>
  </TitleWithDivider>
);

export const PCCEQualification = () => (
  <TitleWithDivider title="응시 자격 및 시험 안내">
    <Container>
      <InnerContainer>
        <InnerTitle>응시 자격 및 시험 안내</InnerTitle>
        <InnerContent>프로그래머스 코딩필수역량인증(PCCE)은 SW 초중급 역량 보유자 및 학습자를 대상으로 합니다.</InnerContent>
        <CardContainer>
          <Card>
            <CardTitle>시험시간</CardTitle>
            <CardDesc>50분</CardDesc>
            <ImgContainer>
              <Image src={clockImg} alt="clock" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>응시료</CardTitle>
            <CardDesc>{getFormattedBaseFee('PCCE')}</CardDesc>
            <ImgContainer>
              <Image src={moneyImg} alt="money" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>인증서 유효기간</CardTitle>
            <CardDesc>7년</CardDesc>
            <ImgContainer>
              <Image src={calendarImg} alt="calendar" />
            </ImgContainer>
          </Card>
        </CardContainer>
      </InnerContainer>
      <InnerContainer>
        <InnerTitle>시험 구성</InnerTitle>
        <InnerContent>3종 개발 언어 중 선택한 1개 언어로 빈칸 채우기, 디버깅, 코드 작성 등 10개 문항을 테스트합니다.</InnerContent>
        <CardContainer>
          <Card>
            <CardTitle>시험과목 (택1)</CardTitle>
            <CardDesc>Python, Java, C++</CardDesc>
            <ImgContainer>
              <Image src={cylinderImg} alt="cylinder" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>문항수</CardTitle>
            <CardDesc>10 문항</CardDesc>
            <ImgContainer>
              <Image src={questionImg} alt="question" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>문제유형</CardTitle>
            <CardDesc>빈칸채우기, 디버깅, 코드작성</CardDesc>
            <ImgContainer>
              <Image src={noteImg} alt="note" />
            </ImgContainer>
          </Card>
        </CardContainer>
      </InnerContainer>
    </Container>
  </TitleWithDivider>
);

export const PCSQLQualification = () => (
  <TitleWithDivider title="응시 자격 및 시험 안내">
    <Container>
      <InnerContainer>
        <InnerTitle>응시 자격 및 시험 안내</InnerTitle>
        <InnerContent>프로그래머스 SQL역량인증(PCSQL)은 SQL 역량 보유자 및 초중고급 학습자를 대상으로 합니다.</InnerContent>
        <CardContainer>
          <Card>
            <CardTitle>시험시간</CardTitle>
            <CardDesc>90분</CardDesc>
            <ImgContainer>
              <Image src={clockImg} alt="clock" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>응시료</CardTitle>
            <CardDesc>{getFormattedBaseFee('PCSQL')}</CardDesc>
            <ImgContainer>
              <Image src={moneyImg} alt="money" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>인증서 유효기간</CardTitle>
            <CardDesc>2년</CardDesc>
            <ImgContainer>
              <Image src={calendarImg} alt="calendar" />
            </ImgContainer>
          </Card>
        </CardContainer>
      </InnerContainer>
      <InnerContainer>
        <InnerTitle>시험 구성</InnerTitle>
        <InnerContent>2종 개발 언어 중 선택한 1개 언어로 코드 작성 5개 문항을 테스트합니다.</InnerContent>
        <CardContainer>
          <Card>
            <CardTitle>시험과목 (택1)</CardTitle>
            <CardDesc>MySQL, Oracle</CardDesc>
            <ImgContainer>
              <Image src={cylinderImg} alt="cylinder" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>문항수</CardTitle>
            <CardDesc>5 문항</CardDesc>
            <ImgContainer>
              <Image src={questionImg} alt="question" />
            </ImgContainer>
          </Card>
          <Card>
            <CardTitle>문제유형</CardTitle>
            <CardDesc>코드작성</CardDesc>
            <ImgContainer>
              <Image src={noteImg} alt="note" />
            </ImgContainer>
          </Card>
        </CardContainer>
      </InnerContainer>
    </Container>
  </TitleWithDivider>
);
